import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersService } from '@/services/users';
import { Users } from '@/types/models';

export const fetchUsers = createAsyncThunk(
    'users/fetchUsers',
    async () => {
        const response = await usersService.fetchUsers();
        return response;
    }
);

export const updateUser = createAsyncThunk(
    'users/updateUser',
    async ({ userId, userData }: { userId: string; userData: Partial<Users> }) => {
        const response = await usersService.updateUser(userId, userData);
        return response;
    }
);

export const activateUser = createAsyncThunk(
    'users/activateUser',
    async (userId: string) => {
        const response = await usersService.activateUser(userId);
        return response;
    }
);

interface UsersState {
    users: Users[];
    currentUser: Users | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    updateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    activateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: UsersState = {
    users: [],
    currentUser: null,
    status: 'idle',
    updateStatus: 'idle',
    activateStatus: 'idle',
    error: null
};

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setUsers: (state, action: PayloadAction<Users[]>) => {
            state.users = action.payload;
        },
        setCurrentUser: (state, action: PayloadAction<Users>) => {
            state.currentUser = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch users
        builder.addCase(fetchUsers.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        });
        builder.addCase(fetchUsers.fulfilled, (state, action) => {
            state.status = 'succeeded';
            if (action.payload.success) {
                state.users = action.payload.data;
                state.currentUser = action.payload.currentUser;
            } else {
                state.error = action.payload.error || 'Failed to fetch users';
            }
        });
        builder.addCase(fetchUsers.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to fetch users';
        });

        // Update user
        builder.addCase(updateUser.pending, (state) => {
            state.updateStatus = 'loading';
            state.error = null;
        });
        builder.addCase(updateUser.fulfilled, (state, action) => {
            state.updateStatus = 'succeeded';
            if (action.payload.success) {
                // Update the user in the users array
                const updatedUser = action.payload.data;
                const index = state.users.findIndex(user => user.id === updatedUser.id);
                if (index !== -1) {
                    state.users[index] = updatedUser;
                }
            } else {
                state.error = action.payload.error || 'Failed to update user';
                state.updateStatus = 'failed';
            }
        });
        builder.addCase(updateUser.rejected, (state, action) => {
            state.updateStatus = 'failed';
            state.error = action.error.message || 'Failed to update user';
        });

        // Activate user
        builder.addCase(activateUser.pending, (state) => {
            state.activateStatus = 'loading';
            state.error = null;
        });
        builder.addCase(activateUser.fulfilled, (state, action) => {
            state.activateStatus = 'succeeded';
            // Refresh users list after activation
        });
        builder.addCase(activateUser.rejected, (state, action) => {
            state.activateStatus = 'failed';
            state.error = action.error.message || 'Failed to activate user';
        });
    }
});

export const { setUsers, setCurrentUser, clearError } = usersSlice.actions;
export default usersSlice.reducer;